import { EMPTY_DATE, partsToIso, type DateParts } from '../../../components/idsk/DateInput';

export const MAX_MEMBERS = 20;

/** The inspected subject as the autocomplete shows it ("Názov, Obec (EDUID …)"). */
export interface WizardSubject {
  id: string;
  label: string;
}

export interface WizardMember {
  id: string;
  displayName: string;
  email: string;
}

export interface WizardState {
  name: string;
  /** The chosen subject; null until a suggestion is chosen (editing the text clears it). */
  subject: WizardSubject | null;
  /** Inspection type code ("1"…"5"); '' = not chosen. */
  type: string;
  /** Inspection task code of the type ("2.3"); '' = none. */
  task: string;
  /** The chosen type has tasks, so a task is required (set from the code lists when the type is chosen). */
  taskRequired: boolean;
  members: WizardMember[];
  leaderId: string | null;
  dateFrom: DateParts;
  dateTo: DateParts;
}

export const EMPTY_WIZARD: WizardState = {
  name: '',
  subject: null,
  type: '',
  task: '',
  taskRequired: false,
  members: [],
  leaderId: null,
  dateFrom: { ...EMPTY_DATE },
  dateTo: { ...EMPTY_DATE },
};

/** Error messages are i18n keys; date errors also say which parts are wrong. */
export interface StepErrors {
  name?: string;
  subject?: string;
  type?: string;
  task?: string;
  members?: string;
  leader?: string;
  dateFrom?: { key: string; parts?: (keyof DateParts)[] };
  dateTo?: { key: string; parts?: (keyof DateParts)[] };
}

export function validateStep1(s: WizardState): StepErrors {
  const errors: StepErrors = {};
  const name = s.name.trim();
  if (!name) errors.name = 'wizard.errors.nameRequired';
  else if (name.length > 255) errors.name = 'wizard.errors.nameTooLong';
  if (!s.subject) errors.subject = 'wizard.errors.subjectRequired';
  if (!s.type) errors.type = 'wizard.errors.typeRequired';
  else if (s.taskRequired && !s.task) errors.task = 'wizard.errors.taskRequired';
  return errors;
}

export function validateStep2(s: WizardState): StepErrors {
  const errors: StepErrors = {};
  if (s.members.length === 0) errors.members = 'wizard.errors.membersRequired';
  else if (s.members.length > MAX_MEMBERS) errors.members = 'wizard.errors.membersTooMany';
  if (s.members.length > 0 && (!s.leaderId || !s.members.some((m) => m.id === s.leaderId))) errors.leader = 'wizard.errors.leaderRequired';
  return errors;
}

export function validateStep3(s: WizardState): StepErrors {
  const errors: StepErrors = {};
  const from = partsToIso(s.dateFrom);
  const to = partsToIso(s.dateTo);
  if (from.kind === 'empty') errors.dateFrom = { key: 'wizard.errors.dateFromRequired' };
  else if (from.kind === 'invalid') errors.dateFrom = { key: 'wizard.errors.dateFromInvalid', parts: from.parts };
  if (to.kind === 'empty') errors.dateTo = { key: 'wizard.errors.dateToRequired' };
  else if (to.kind === 'invalid') errors.dateTo = { key: 'wizard.errors.dateToInvalid', parts: to.parts };
  if (from.kind === 'valid' && to.kind === 'valid' && from.iso > to.iso) errors.dateTo = { key: 'wizard.errors.dateOrder' };
  return errors;
}

export const STEP_VALIDATORS = [validateStep1, validateStep2, validateStep3] as const;

export function hasErrors(e: StepErrors): boolean {
  return Object.keys(e).length > 0;
}

/** 1-based number of the first step with errors, or 4 (summary) when everything is valid. */
export function firstInvalidStep(s: WizardState): 1 | 2 | 3 | 4 {
  if (hasErrors(validateStep1(s))) return 1;
  if (hasErrors(validateStep2(s))) return 2;
  if (hasErrors(validateStep3(s))) return 3;
  return 4;
}
